import {
  calculateDynamicReturnsPrice,
  getFinalSaleReturnsPrice,
  getPackageProtectionPrice,
} from "@redotech/redo-model/coverage-calculator/calculator-utils";
import { getInternationalPrice } from "@redotech/redo-model/coverage-calculator/country-check";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyCartItem } from "@redotech/shopify-client/ajax";

export function getCalculatedCoveragePrice({
  basePriceDollars,
  autocheck,
  onTapcart,
  cartValueReturnableItems,
  cartValuePackageProtectionItems,
  finalSaleReturnsCartValue,
  cartItems,
  numValidReturnProducts,
  country,
  cartHasSubscriptionItem,
}: {
  basePriceDollars: number;
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
  cartValueReturnableItems: number;
  cartValuePackageProtectionItems: number;
  finalSaleReturnsCartValue: number;
  cartItems: ShopifyCartItem[] | undefined;
  numValidReturnProducts: number;
  country?: string | null;
  cartHasSubscriptionItem: boolean;
}) {
  let returnCost = 0;

  // tapcart does everything in dollars
  const basePriceCents = onTapcart ? basePriceDollars : basePriceDollars * 100;

  const returnCartValue = onTapcart
    ? cartValueReturnableItems
    : cartValueReturnableItems / 100;

  const fsrCartValue = onTapcart
    ? finalSaleReturnsCartValue
    : finalSaleReturnsCartValue / 100;

  if (autocheck.dynamicReturnsPricing && cartItems) {
    returnCost = calculateDynamicReturnsPrice(
      autocheck.dynamicReturnsPricing,
      basePriceDollars,
      numValidReturnProducts,
      returnCartValue,
    );
    returnCost = onTapcart ? returnCost : returnCost * 100;
  } else {
    returnCost = autocheck.pricePerOrder
      ? onTapcart
        ? parseFloat(autocheck.pricePerOrder)
        : parseFloat(autocheck.pricePerOrder) * 100
      : basePriceCents; // realistically we should always have a "pricePerOrder" so we shouldn't reach this.
  }

  if (country && autocheck.internationalReturnsPricing) {
    returnCost = getInternationalPrice(
      returnCost,
      country,
      autocheck.countries,
      autocheck.internationalReturnsPricing,
    );
  }

  if (autocheck.finalSaleReturns.coverageEnabled) {
    let finalSaleReturnsCost = getFinalSaleReturnsPrice({
      cartTotal: fsrCartValue,
      pricingRuleSets: autocheck.finalSaleReturns.pricingRuleSets,
      shippingLabelCoveragePrice:
        autocheck.finalSaleReturns.shippingLabelCoveragePrice,
      country,
      minPrice: autocheck.finalSaleReturns.minPrice,
      maxPrice: autocheck.finalSaleReturns.maxPrice,
      backupPercentage: autocheck.finalSaleReturns.percentage,
    });
    finalSaleReturnsCost = onTapcart
      ? finalSaleReturnsCost
      : finalSaleReturnsCost * 100;

    // Since final sale returns price bakes in the price for returns, we can simply take
    // the max of the two prices to determine how much to charge for returns on mixed carts.
    // Final sale returns does not support dynamic pricing
    returnCost = Math.max(returnCost, finalSaleReturnsCost);
  }

  const packageProtectionCartValue = onTapcart
    ? cartValuePackageProtectionItems
    : cartValuePackageProtectionItems / 100;

  let packageProtectionCost = getPackageProtectionPrice({
    cartTotal: packageProtectionCartValue,
    pricingRuleSets: autocheck.packageProtection.pricingRuleSets,
    country,
    minPrice: autocheck.packageProtection.minPrice,
    maxPrice: autocheck.packageProtection.maxPrice,
    backupPercentage: autocheck.packageProtection.percentage,
    fixedSubscriptionPrice: autocheck.packageProtection.fixedSubscriptionPrice,
    cartHasSubscriptionItem,
  });
  packageProtectionCost = onTapcart
    ? packageProtectionCost
    : Math.round(packageProtectionCost * 100); // round to nearest cent

  // tapcart works in dollars so we don't want to round to the nearest dollar
  if (!onTapcart) {
    returnCost = Math.round(returnCost);
  }

  return {
    returnCost,
    packageProtectionCost,
    bothCost: packageProtectionCost + returnCost,
  };
}
